/**
 * „Inaczej niż zwykle" (T-B2) — the dates unlike their weekday and the blocks still ahead: five
 * weeks from this one, each day marked with what is unusual about it, and under them the list with
 * the act that undoes each line („Przywróć wzorzec" for an exception, „Usuń" for a block).
 *
 * A day from today on is a button: it opens the exception dialog on that date, already holding
 * whatever the date says. A day behind today is just a number. The exception's words stand in the
 * day in the accent's INK (a real state, the canon's `accent-ink`), the block in its hatch swatch
 * and a word — never a colour alone.
 *
 * The month is a grid: one Tab stop, the arrows walk the days (a week up and down), Home and End
 * reach a week's ends; the weekday heads are its column headers. A past day is a cell that takes
 * focus and says it cannot be opened. Undoing a line moves focus to the line after it — or the
 * list's heading when it was the last — once the line is gone.
 *
 * The card is drawn only once the hours have answered: its height is the list's, and a wait drawn
 * without the list grew by that list when it landed (128px with two lines, measured 27 September
 * 2026). The tab stands the week card beside an empty column while it waits.
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '~/shared/ui/EmptyState';
import { CalendarMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { ScreenHeaderPlus } from '~/shared/ui/ScreenHeader';
import form from '~/shared/ui/formDialog.module.css';

import { fullDate, monthSpan } from '../model/calendarWords';
import type { Deviation, DeviationMark } from '../model/scheduleDeviations';
import { deviationWords, markWords } from '../model/scheduleWords';
import type { CivilDay } from '../model/trainerClock';
import frame from './bookingTab.module.css';
import styles from './ScheduleTab.module.css';

interface DeviationsCardProps {
  readonly days: readonly CivilDay[];
  readonly marks: ReadonlyMap<CivilDay, DeviationMark>;
  readonly deviations: readonly Deviation[];
  readonly today: CivilDay;
  readonly refusal: string | null;
  readonly onOpenDay: (day: CivilDay) => void;
  readonly onAddException: () => void;
  readonly onAddBlock: () => void;
  readonly onUndo: (deviation: Deviation) => void;
  /** The line whose undo is being asked about, while the question stands. */
  readonly undoing: Deviation | null;
}

const STEP: Readonly<Record<string, number>> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -7,
  ArrowDown: 7,
};

const keyOf = (deviation: Deviation) =>
  deviation.kind === 'exception' ? deviation.date : `block-${String(deviation.block.id)}`;

export function DeviationsCard({
  days,
  marks,
  deviations,
  today,
  refusal,
  onOpenDay,
  onAddException,
  onAddBlock,
  onUndo,
  undoing,
}: DeviationsCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const first = days[0] ?? today;
  const last = days[days.length - 1] ?? today;
  const card = useRef<HTMLElement>(null);
  const [active, setActive] = useState<CivilDay>(today);
  const focusDay = days.includes(active) ? active : days.includes(today) ? today : first;
  const weeks = Array.from({ length: Math.ceil(days.length / 7) }, (_, index) =>
    days.slice(index * 7, index * 7 + 7),
  );

  const walk = (event: KeyboardEvent, day: CivilDay) => {
    const at = days.indexOf(day);
    const step = STEP[event.key];
    const next =
      step !== undefined
        ? at + step
        : event.key === 'Home'
          ? at - (at % 7)
          : event.key === 'End'
            ? at - (at % 7) + 6
            : null;
    if (next === null) return;
    event.preventDefault();
    const target = days[next];
    if (target === undefined) return;
    setActive(target);
    card.current?.querySelector<HTMLElement>(`[data-day="${target}"]`)?.focus();
  };

  // The line an undo was asked about, until the question is answered: gone → focus moves on.
  const asked = useRef<{ readonly key: string; readonly index: number } | null>(null);
  useEffect(() => {
    if (undoing !== null) {
      const key = keyOf(undoing);
      asked.current = { key, index: deviations.findIndex((entry) => keyOf(entry) === key) };
      return;
    }
    const line = asked.current;
    if (line === null) return;
    asked.current = null;
    if (deviations.some((entry) => keyOf(entry) === line.key)) return;
    const buttons = card.current?.querySelectorAll<HTMLElement>('[data-undo]') ?? [];
    const next = buttons[Math.min(line.index, buttons.length - 1)];
    (
      next ??
      card.current?.querySelector<HTMLElement>('#booking-deviations-list') ??
      card.current?.querySelector<HTMLElement>('#booking-deviations-title')
    )?.focus();
  });

  return (
    <section
      ref={card}
      className={`${String(frame.card)} ${String(styles.deviations)}`}
      aria-labelledby="booking-deviations-title"
    >
      <div className={styles.cardHead}>
        <h2 id="booking-deviations-title" className={frame.cardTitle} tabIndex={-1}>
          {t('web:booking.trainer.schedule.deviationsHead')}
        </h2>
        <div className={styles.headActs}>
          <button type="button" className={styles.tinyCard} onClick={onAddException}>
            <ScreenHeaderPlus className={styles.tinyGlyph} />
            {t('web:booking.schedule.addException')}
          </button>
          <button type="button" className={styles.tinyCard} onClick={onAddBlock}>
            <ScreenHeaderPlus className={styles.tinyGlyph} />
            {t('web:booking.schedule.addBlock')}
          </button>
        </div>
      </div>

      <div className={styles.monthHead}>
        <span className={styles.monthLabel}>{monthSpan(first, last, locale)}</span>
        <span className={styles.legend} aria-hidden="true">
          <span>
            <i className={styles.legendDot} />
            {t('web:booking.schedule.legendException')}
          </span>
          <span>
            <i className={styles.hatch} />
            {t('web:booking.schedule.legendBlock')}
          </span>
        </span>
      </div>

      <div className={styles.month} role="grid" aria-label={t('web:booking.schedule.monthLabel')}>
        <div className={styles.monthRow} role="row">
          {[1, 2, 3, 4, 5, 6, 7].map((weekday) => (
            <span
              key={weekday}
              className={styles.monthWeekday}
              role="columnheader"
              aria-label={t(`web:weekdays.full.${String(weekday)}`)}
            >
              {t(`web:weekdays.short.${String(weekday)}`)}
            </span>
          ))}
        </div>
        {weeks.map((week) => (
          <div key={week[0]} className={styles.monthRow} role="row">
            {week.map((day) => {
              const mark = marks.get(day);
              const words = markWords(mark, t);
              const number = String(Number(day.slice(8, 10)));
              const past = day < today;
              const state = [
                words,
                mark?.blocked === true ? t('web:booking.schedule.cellBlock') : null,
              ]
                .filter(Boolean)
                .join(', ');
              const content = (
                <>
                  <span
                    className={
                      day === today
                        ? styles.cellToday
                        : past
                          ? styles.cellNumberPast
                          : styles.cellNumber
                    }
                  >
                    {number}
                  </span>
                  {words === null ? null : <span className={styles.cellOnly}>{words}</span>}
                  {mark?.blocked === true && words === null ? (
                    <span className={styles.cellBlock}>
                      <i className={styles.hatchSmall} />
                      {t('web:booking.schedule.cellBlock')}
                    </span>
                  ) : null}
                </>
              );
              const roving = {
                'data-day': day,
                tabIndex: day === focusDay ? 0 : -1,
                onKeyDown: (event: KeyboardEvent) => {
                  walk(event, day);
                },
                onFocus: () => {
                  setActive(day);
                },
              };
              if (past) {
                return (
                  <span
                    key={day}
                    role="gridcell"
                    aria-disabled="true"
                    aria-label={fullDate(day, locale)}
                    className={`${String(styles.cell)} ${String(styles.cellPast)}`}
                    {...roving}
                  >
                    {content}
                  </span>
                );
              }
              return (
                <span key={day} role="gridcell" className={styles.gridCell}>
                  <button
                    type="button"
                    className={styles.cell}
                    aria-label={
                      state === ''
                        ? t('web:booking.schedule.openDay', { date: fullDate(day, locale) })
                        : `${t('web:booking.schedule.openDay', { date: fullDate(day, locale) })} (${state})`
                    }
                    onClick={() => {
                      onOpenDay(day);
                    }}
                    {...roving}
                  >
                    {content}
                  </button>
                </span>
              );
            })}
          </div>
        ))}
      </div>

      {refusal === null ? null : (
        <p className={form.refusal} role="alert">
          {refusal}
        </p>
      )}

      {deviations.length === 0 ? (
        <>
          <EmptyState
            inline
            className={styles.deviationsEmpty}
            tone="positive"
            mark={<CalendarMarkIcon />}
            title={t('web:booking.trainer.schedule.deviationsEmpty')}
          />
          {/* X1's second line. The inline state draws a title alone, so the hint stands under it. */}
          <p className={styles.deviationsHint}>
            {t('web:booking.trainer.schedule.deviationsEmptyHint')}
          </p>
        </>
      ) : (
        <ul
          id="booking-deviations-list"
          className={styles.deviationList}
          tabIndex={-1}
          aria-labelledby="booking-deviations-title"
        >
          {deviations.map((deviation) => {
            const words = deviationWords(deviation, t, locale);
            const key = keyOf(deviation);
            const act =
              deviation.kind === 'exception'
                ? {
                    label: t('web:booking.schedule.restore'),
                    aria: 'web:booking.schedule.restoreLabel',
                  }
                : {
                    label: t('web:booking.schedule.remove'),
                    aria: 'web:booking.schedule.removeLabel',
                  };
            return (
              <li key={key} className={styles.deviation}>
                <div className={styles.deviationText}>
                  <span className={styles.deviationTitle}>{words.title}</span>
                  <span className={form.note}>{words.meta}</span>
                </div>
                <button
                  type="button"
                  className={styles.tinyGhost}
                  data-undo
                  aria-label={t(act.aria, { title: words.title })}
                  onClick={() => {
                    onUndo(deviation);
                  }}
                >
                  {act.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
